namespace Nbs.Xlr8PageTemplate.Navigation {
  export function getMenuItemElements(
    item: MenuItem,
    clickHandler: Utilities.ElementBuilderEventHandler<MouseEvent>,
    hasChildren = false
  ): (Node | undefined)[] {
    const dropdownIcon =  hasChildren ? Utilities.getIconElement('arrow_drop_down') : undefined;

    const label = item.label || '';

    const childItems = (item as Menu).childItems;
    if (childItems && !dropdownIcon) {
      const childList = Utilities.createElement({
        tag: 'div',
        attributes: {
          role: getContainerRole(childItems),
        },
        children:
          (childItems as Link[]).map(child =>
            Utilities.createElement({
              tag: 'div',
              classes: ['xpt-list-link'],
              children: child.url ?
                [Utilities.createElement({
                  classes: ['xpt-header-menu-link'],
                  children: [Utilities.createElement({
                    tag: 'a',
                    attributes: getLeafAttributes(child, childItems, false),
                    eventHandlers: { click: clickHandler },
                    children: getMenuItemElements(child, clickHandler),
                  })]
                })] : getMenuItemElements(child, clickHandler),
          }))
      });

      const title = Utilities.createElement({
        tag: 'span',
        classes: ['xpt-header-menu-title'],
        children: [document.createTextNode(label)]
      });

      return [title, childList];
    }

    const openInNewTabIcon = (item as Link).openInTab && !dropdownIcon ? Utilities.getIconElement('open_in_new', 'Open in new tab', undefined, 'open-in-new-tab-icon') : undefined;

    if (item.displayMode === DisplayMode.TextOnly) {
      return [document.createTextNode(label), dropdownIcon, openInNewTabIcon];
    }

    const icon = Utilities.getIconElement(item.icon);

    if (item.displayMode === DisplayMode.IconOnly) {
      return [icon, dropdownIcon];
    }

    return [icon, document.createTextNode(label), dropdownIcon, openInNewTabIcon];
  }

  export function getLinkOnlyAttributes(item: Link): Utilities.ElementBuilderAttributes {
    return {
      'aria-disabled': item.isDisabled ? true : undefined,
      'aria-label': item.label,
      'data-menu-item-id': item.id,
      href: item.isDisabled ? undefined : (item.url || '#'),
      rel: item.openInTab ? 'noopener' : undefined, // https://web.dev/external-anchors-use-rel-noopener/
      role: item.url ? undefined : 'button',
      target: item.openInTab ? '_blank' : undefined,
      title: item.displayMode === DisplayMode.IconOnly ? item.label : undefined
    };
  }

  export function getLeafAttributes(
    item: MenuItem,
    containerRoleOrSiblings: 'menu' | 'list' | MenuItem[],
    isMobile = false
  ): Utilities.ElementBuilderAttributes {
    return {
      'aria-checked': (item as Link).isSelected,
      'aria-disabled': (item as Link).isDisabled ? true : undefined,
      'aria-label': item.label,
      'data-menu-item-id': item.id,
      href: isEnabledLinkLeafMenuItem(item) ? (item as Link).url : undefined,
      id: isMobile ? (item as Link).mobileLinkId : (item as Link).desktopLinkId,
      rel: (item as Link).openInTab ? 'noopener' : undefined, // https://web.dev/external-anchors-use-rel-noopener/
      role: getLeafRole(item, containerRoleOrSiblings),
      target: (item as Link).openInTab ? '_blank' : undefined,
      title: item.displayMode === DisplayMode.IconOnly ? item.label : undefined
    };
  }

  export function handleClickEvent(
    event: MouseEvent,
    menuItems?: MenuItem[],
    clickHandler?: ((menuItem: MenuItem) => boolean)
  ): boolean {
    if (Utilities.isDisabled(event.target as HTMLElement)) {
      Utilities.stopEvent(event);
      return false;
    }

    if (event.ctrlKey) {
      return true;
    }

    if (menuItems) {
      const menuItemId = getClickedMenuId(event) ?? '';
      if (menuItemId) {
        let menuItem: MenuItem | undefined;

        const parentNbsMenuElement = (event.target as HTMLElement).closest('nbs-menu');

        if (clickHandler || parentNbsMenuElement) {
          menuItem = getMenuItem(menuItems, menuItemId);
        }

        if (parentNbsMenuElement && menuItem && !(menuItem as Link).openInTab) {
          Menu.setActiveMenuItem(menuItemId);
        }

        if (Flyout.openFlyoutId && !(event.target as HTMLElement).closest('xpt-flyout-menu')) {
          Flyout.closeFlyoutMenu();
        }

        if (clickHandler && menuItem && clickHandler(menuItem)) {
          // if click handler handles the event, then stop any propagation
          // if not the regular handling of this link will take place
          Utilities.stopEvent(event);
          return false;
        }
      }
    }

    return true;
  }

  function getClickedMenuId(event: MouseEvent) {
    // the clickable element or its parent has an attribute that identifies the menu/link ID
    let element = event.target as HTMLElement;
    let menuItemId = element.getAttribute('data-menu-item-id');
    while (!menuItemId && element) {
      element = element.parentElement as HTMLElement;
      if (element) {
        menuItemId = element.getAttribute('data-menu-item-id');
      }
    }
    return menuItemId;
  }

  export function getMenuItem(menuItems?: MenuItem[], menuItemId?: string): MenuItem | undefined {
    if (menuItemId && menuItems) {
      for (const menuItem of menuItems) {
        if (menuItem.id === menuItemId && !((menuItem as Link).isDisabled)) {
          return menuItem;
        }
        const childItems = (menuItem as Menu).childItems;
        if (childItems) {
          const childItem = getMenuItem(childItems, menuItemId);
          if (childItem) {
            return childItem;
          }
        }
      }
    }
    return undefined;
  }

  export function isParent(menuItem: MenuItem) {
    return !!(menuItem as Menu).childItems;
  }

  export function isRadioLeafMenuItem(menuItem: MenuItem) {
    return !isParent(menuItem) && (menuItem as Link).isSelected !== undefined && (menuItem as Link).isSelected !== null;
  }

  export function isEnabledLinkLeafMenuItem(menuItem: MenuItem) {
    return !isParent(menuItem) && (menuItem as Link).url && !(menuItem as Link).isDisabled;
  }

  export function isTextLeafMenuItem(menuItem: MenuItem) {
    return !isParent(menuItem) && !isRadioLeafMenuItem(menuItem) && !isEnabledLinkLeafMenuItem(menuItem);
  }

  export function getContainerRole(menuItems: MenuItem[]): 'menu' | 'list' {
    return menuItems.some(x => !isTextLeafMenuItem(x)) ? 'menu' : 'list';
  }

  export function getLeafRole(
    menuItem: MenuItem,
    containerRoleOrSiblings: 'menu' | 'list' | MenuItem[]
  ): 'menuitem' | 'menuitemradio' | 'listitem' {
    if (typeof containerRoleOrSiblings !== 'string') {
      return getLeafRole(menuItem, containerRoleOrSiblings.some(x => !isTextLeafMenuItem(x)) ? 'menu' : 'list');
    }

    if (containerRoleOrSiblings === 'list') {
      return 'listitem';
    }

    if (isRadioLeafMenuItem(menuItem)) {
      return 'menuitemradio';
    }

    return 'menuitem';
  }

  export function getCommonLeafClasses(menuItem: MenuItem) {
    return [
      (menuItem as Link).isDisabled ? 'xpt-disabled' : '',
    ];
  }
}
